import { readonly, ref } from 'vue'
import type { RouteLocationNormalized, Router } from 'vue-router'
import { capturePageSnapshot } from '@/composables/usePageSnapshot'

/**
 * Sens et activation des transitions de page.
 *
 * Les deux sorties de l'app (la racine dans `App.vue`, le contenu d'onglets
 * dans `TabsPage` et `ChildModeTabsPage`) partagent cet état : une navigation
 * n'anime qu'une sortie à la fois, celle dont la clé change, l'autre ne voit
 * passer aucun changement d'enfant.
 *
 * Le sens se lit dans `history.state.position`, le compteur d'entrées que
 * vue-router écrit lui-même. Au moment où une garde s'exécute :
 *
 * - une poussée n'a pas encore appelé `pushState`, la position vaut donc
 *   encore celle de la page qu'on quitte, donc « en avant » ;
 * - un retour est arrivé par `popstate`, le navigateur a DÉJÀ restauré
 *   l'entrée précédente, la position a baissé, donc « en arrière ».
 *
 * Ce que ce compteur ne voit pas : un retour joué en `replace`. Huit des
 * treize boutons retour de l'app portent un `fallback` et remplacent l'entrée
 * courante ; la position ne bouge pas et le sens serait faux, ce qui se voit
 * énormément. D'où `markBackNavigation()`, que BackButton appelle avant de
 * naviguer, quel que soit le verbe qu'il emploie ensuite.
 */

/**
 * Racines d'onglet, côté parent et côté enfant. Passer de l'une à l'autre ne
 * s'anime PAS comme une poussée : ces destinations sont côte à côte, pas
 * empilées, et la bascule instantanée est une décision mesurée sur appareil
 * (cf. project-perf-instant-tabs).
 */
const TAB_ROOTS = new Set([
  '/tabs/home',
  '/tabs/tasks',
  '/tabs/rewards',
  '/tabs/stats',
  '/tabs/settings',
  '/child-mode/tasks',
  '/child-mode/rewards',
  '/child-mode/allowance',
])

/**
 * Les groupes de PANNEAUX : des onglets qui partagent la même barre du haut et
 * le même enfant (l'Accueil, lui, parle de toute la famille mais garde la
 * barre), et dont seul le contenu change. Eux glissent, parce qu'un
 * glissement dit la seule chose vraie à leur sujet, qu'ils sont voisins sur une
 * même rangée. L'ordre du tableau EST l'ordre de la barre d'onglets : c'est lui
 * qui décide de quel côté le panneau entrant arrive.
 *
 * Réglages n'en fait pas partie : rien n'y est par enfant, la barre du haut
 * disparaît, il n'y a donc pas de rangée à parcourir. Cette bascule reste
 * instantanée.
 */
const PANE_GROUPS: string[][] = [
  ['/tabs/home', '/tabs/tasks', '/tabs/rewards', '/tabs/stats'],
  ['/child-mode/tasks', '/child-mode/rewards', '/child-mode/allowance'],
]

/**
 * Durée de validité d'un retour déclaré. Un bouton dont le gestionnaire
 * renonce à naviguer ne doit pas laisser la marque traîner jusqu'à la
 * navigation suivante, qui serait alors jouée à l'envers.
 */
const MARK_TTL_MS = 1_000

const transitionName = ref<PageTransitionName>('page-forward')
const transitionEnabled = ref(false)

/**
 * Faux tant qu'une page glisse.
 *
 * Une page qui met le focus sur son champ dès `onMounted` ouvre le clavier au
 * premier tiers du glissement : la barre monte, la WebView se redimensionne et
 * WebKit fait défiler vers le champ, le tout PENDANT une animation de
 * `transform`. Trois travaux lourds sur les mêmes frames, et la poussée
 * saccade. Deux écrans le faisaient sans le savoir, un troisième s'en était
 * tiré avec un `setTimeout(300)` écrit à la main, 40 ms trop court.
 *
 * Le signal vient d'`@after-enter` sur la `<Transition>` racine, pas d'un
 * minuteur : c'est le vrai moment, et il tient si la durée du CSS change.
 */
const pageSettled = ref(true)
let settleTimer: ReturnType<typeof setTimeout> | null = null

export type PageTransitionName = 'page-forward' | 'page-back' | 'pane-next' | 'pane-prev'

let lastPosition = 0
let backMarkedAt = 0

function historyPosition(): number {
  const state = window.history.state as { position?: number } | null
  return typeof state?.position === 'number' ? state.position : 0
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Déclare que la navigation qui suit est un retour. À appeler AVANT de
 * naviguer, depuis tout ce qui recule sans passer par l'historique.
 */
export function markBackNavigation(): void {
  backMarkedAt = Date.now()
}

/**
 * Recule d'un écran en REMPLAÇANT l'entrée courante, et le dit.
 *
 * `router.replace()` seul ne déplace pas `history.state.position` : un retour
 * joué comme ça est indiscernable d'une poussée, et se voit glisser dans le
 * mauvais sens. BackButton connaît le piège et appelle `markBackNavigation()` ;
 * les retours PROGRAMMATIQUES, eux, l'oubliaient — sept dans les seuls
 * parcours « rejoindre une famille ». Ce raccourci existe pour que le nom de
 * l'appel dise l'intention, plutôt que de compter sur la mémoire de chacun.
 */
export function replaceBack(router: Router, to: string): Promise<unknown> {
  markBackNavigation()
  return router.replace(to)
}

/** Miroir de `.page-forward-enter-active` dans index.css. Filet, pas horloge. */
const SETTLE_FALLBACK_MS = 340 + 160

function beginPageTransition(enabled: boolean): void {
  if (settleTimer) { clearTimeout(settleTimer); settleTimer = null }
  if (!enabled) {
    pageSettled.value = true
    return
  }
  pageSettled.value = false
  // Filet : une navigation qui en chasse une autre peut priver la sortante de
  // son `after-enter`. Sans ce minuteur, ce qui attend attendrait pour de bon.
  settleTimer = setTimeout(endPageTransition, SETTLE_FALLBACK_MS)
}

/** À câbler sur `@after-enter` de la `<Transition>` racine. */
export function endPageTransition(): void {
  if (settleTimer) { clearTimeout(settleTimer); settleTimer = null }
  pageSettled.value = true
}

export function usePageTransition() {
  return { transitionName, transitionEnabled, pageSettled: readonly(pageSettled), endPageTransition }
}

/** Lecture seule, pour `onPageEnterSettled`. */
export const isPageSettled = readonly(pageSettled)

/**
 * Où se trouve ce chemin dans sa rangée de panneaux, et dans laquelle. `-1`
 * signifie « pas un panneau ».
 */
function paneIndex(path: string): { group: number; index: number } {
  for (let g = 0; g < PANE_GROUPS.length; g += 1) {
    const index = PANE_GROUPS[g].indexOf(path)
    if (index !== -1) return { group: g, index }
  }
  return { group: -1, index: -1 }
}

/**
 * Décide du sens et de l'activation. Pur, donc testable sans routeur ni
 * historique : c'est ici que vivent toutes les règles.
 */
export function resolvePageTransition(input: {
  toPath: string
  fromPath: string
  /** Faux au tout premier rendu : rien à faire glisser contre rien. */
  hasFrom: boolean
  goingBack: boolean
  reducedMotion: boolean
}): { name: PageTransitionName; enabled: boolean } {
  const samePlace = input.toPath === input.fromPath
  const inert = input.reducedMotion || !input.hasFrom || samePlace

  const to = paneIndex(input.toPath)
  const from = paneIndex(input.fromPath)
  const samePaneRow = to.group !== -1 && to.group === from.group

  // Deux panneaux d'une même rangée : on glisse du côté où ils se trouvent
  // vraiment l'un par rapport à l'autre, jamais selon le sens de l'historique.
  // Aller de Récompenses à Tâches est un retour VERS LA GAUCHE même quand
  // c'est une poussée pour le routeur.
  if (samePaneRow) {
    return {
      name: to.index > from.index ? 'pane-next' : 'pane-prev',
      enabled: !inert,
    }
  }

  const bothTabRoots = TAB_ROOTS.has(input.toPath) && TAB_ROOTS.has(input.fromPath)
  return {
    name: input.goingBack ? 'page-back' : 'page-forward',
    enabled: !inert && !bothTabRoots,
  }
}

/**
 * La sortie qui va changer d'enfant : la racine quand l'enregistrement de
 * premier niveau change, la sortie d'onglets sinon. C'est de celle-là qu'il
 * faut copier la page, et c'est dans celle-là que le glissé cherchera.
 */
function outletFor(to: RouteLocationNormalized, from: RouteLocationNormalized): HTMLElement | null {
  const rootChanges = to.matched[0]?.path !== from.matched[0]?.path
  return document.querySelector<HTMLElement>(rootChanges ? '.app-root' : '.page-outlet')
}

export function installPageTransitions(router: Router): void {
  lastPosition = historyPosition()

  router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const marked = backMarkedAt !== 0 && Date.now() - backMarkedAt < MARK_TTL_MS
    backMarkedAt = 0

    const { name, enabled } = resolvePageTransition({
      toPath: to.path,
      fromPath: from.path,
      hasFrom: from.matched.length > 0,
      goingBack: marked || historyPosition() < lastPosition,
      reducedMotion: prefersReducedMotion(),
    })

    transitionName.value = name
    transitionEnabled.value = enabled
    beginPageTransition(enabled)

    // Sur une poussée animée seulement : on garde une copie de la page qu'on
    // quitte, que le glissé de retour posera sous la page tirée. Le DOM montre
    // encore cette page à cet instant. Les bascules d'onglet ne passent jamais
    // ici, elles ne paient donc pas le `cloneNode`.
    if (enabled && name === 'page-forward') {
      capturePageSnapshot(outletFor(to, from), from.path)
    }
  })

  router.afterEach(() => {
    lastPosition = historyPosition()
  })
}
